import { useCallback, useEffect, useRef, useState } from "react";
import {
  MAXES_CACHE_TTL_MS,
  fetchMaxesDetailed,
  type ApiFetcher,
  type CurrentMax,
} from "@heraion/shared";

// Shared MAX data layer — TTL cache + in-flight coalescing + the
// setAt-keeping `maxMeta` map — lifted verbatim from apps/mobile's
// MaxesContext so the mobile Provider and the web-rnw Maxes console read
// ONE implementation. Mobile keeps wrapping this in a Context (the
// post-save MAX-bump pill + plan runner consume the singleton); web calls
// the hook directly.
//
// Storage shape mirrors the API: { exerciseId → kg }, kept canonical (kg)
// per Profile.unitsPreference's "convert at the edge" model — consumers
// format for display. `maxMeta` keeps the same data plus `setAt` (the MAX-
// regression staleness gate needs it), derived from the same single fetch.
//
// `loaded` stays false until the first SUCCESSFUL fetch so the MAX-bump
// check can skip evaluation against an empty map (else every weighted set
// reads as a new MAX). On fetch failure the previously-stored map is kept
// (covers a transient blip after a prior success). apiFetch goes through a
// ref so a fresh literal per render never re-kicks the boot fetch.
/** "failed" only until a read succeeds: once the MAXes are known, a later
 *  failure (offline) keeps serving them as loaded. */
export type MaxesStatus = "loading" | "loaded" | "failed";

export type MaxesData = {
  maxes: Map<string, number>;
  maxMeta: Map<string, CurrentMax>;
  loaded: boolean;
  status: MaxesStatus;
  /** Returns a fresh-or-cached map; dedupes concurrent calls on the same
   *  in-flight promise. Use for the read path. */
  getFresh: () => Promise<Map<string, number>>;
  /** The same read as `getFresh`, but handing back the DETAILED map — value
   *  plus `setAt` plus `source`. Shares the TTL and the in-flight promise, so
   *  it costs no extra request; it exists because reading `maxMeta` off the
   *  render closure right after awaiting `getFresh` can hand back the PREVIOUS
   *  fetch (state has not re-rendered yet). A consumer that needs the source
   *  to decide whether it may propose a load (resolveDefaultWeight's effort
   *  arm, PG-9) would then silently see "unknown" and decline. */
  getFreshDetailed: () => Promise<Map<string, CurrentMax>>;
  /** Bypasses the TTL. Use after a mutation (PUT/DELETE) so the next read
   *  sees the new value without waiting for MAXES_CACHE_TTL_MS. */
  forceRefresh: () => Promise<Map<string, number>>;
};

export type UseMaxesDataOptions = { apiFetch: ApiFetcher };

export function useMaxesData(opts: UseMaxesDataOptions): MaxesData {
  const [maxes, setMaxes] = useState<Map<string, number>>(new Map());
  const [maxMeta, setMaxMeta] = useState<Map<string, CurrentMax>>(new Map());
  // The detailed map kept OUT of React state too, so an awaited read sees the
  // fetch that just landed rather than the closure's stale render value.
  const metaRef = useRef<Map<string, CurrentMax>>(new Map());
  const [status, setStatus] = useState<MaxesStatus>("loading");
  const lastFetchedRef = useRef<number>(0);
  const inFlightRef = useRef<Promise<Map<string, number>> | null>(null);

  const apiRef = useRef(opts.apiFetch);
  useEffect(() => {
    apiRef.current = opts.apiFetch;
  });

  const fetchAndStore = useCallback(async (): Promise<Map<string, number>> => {
    if (inFlightRef.current) return inFlightRef.current;
    const promise = (async () => {
      try {
        const detailed = await fetchMaxesDetailed(apiRef.current);
        // Derive the canonical kg-only map from the same fetch so existing
        // consumers (MAX-bump pill, plan runner) are untouched.
        const next = new Map<string, number>();
        for (const [slug, m] of detailed) next.set(slug, m.valueKg);
        setMaxes(next);
        setMaxMeta(detailed);
        metaRef.current = detailed;
        lastFetchedRef.current = Date.now();
        setStatus("loaded");
        return next;
      } catch {
        // Best-effort: leaving `loaded` false signals the consumer to skip
        // MAX-bump evaluation. The map already in state stays.
        setStatus((prev) => (prev === "loaded" ? "loaded" : "failed"));
        return maxes;
      } finally {
        inFlightRef.current = null;
      }
    })();
    inFlightRef.current = promise;
    return promise;
  }, [maxes]);

  // Fire one fetch on mount. Mobile mounts the Provider inside TabShell
  // (post Cognito sign-in) so apiFetch has a token; web mounts the hook
  // inside the authed AppShell.
  useEffect(() => {
    void fetchAndStore();
    // Empty deps on purpose: only the boot fetch here. Later refreshes flow
    // through getFresh / forceRefresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getFresh = useCallback(async (): Promise<Map<string, number>> => {
    const ageMs = Date.now() - lastFetchedRef.current;
    if (lastFetchedRef.current > 0 && ageMs < MAXES_CACHE_TTL_MS) {
      return maxes;
    }
    return fetchAndStore();
  }, [maxes, fetchAndStore]);

  const getFreshDetailed = useCallback(async (): Promise<
    Map<string, CurrentMax>
  > => {
    await getFresh();
    return metaRef.current;
  }, [getFresh]);

  const forceRefresh = useCallback(async (): Promise<Map<string, number>> => {
    lastFetchedRef.current = 0;
    return fetchAndStore();
  }, [fetchAndStore]);

  return {
    maxes,
    maxMeta,
    loaded: status === "loaded",
    status,
    getFresh,
    getFreshDetailed,
    forceRefresh,
  };
}
